import {
  ChatStatus,
  ChatMessageRole,
} from '@rematter/frontend-lib-api-client/grpc/assistant/v1/assistant_pb';
import { AccountType } from '@rematter/frontend-lib-api-client/grpc/crm/v1/account_pb';
import { FilePermissionAction } from '@rematter/frontend-lib-api-client/grpc/files/v1/file_pb';
import {
  BillStatus,
  BillType,
  InvoiceStatus,
  InvoiceType,
} from '@rematter/frontend-lib-api-client/grpc/finance/v1/domain_pb';
import {
  PaymentMethod,
  PaymentTermCode,
} from '@rematter/frontend-lib-api-client/grpc/finance/v1/finance_pb';
import {
  QuantityCaptureMethod,
  InventoryTagStatus,
  TagType,
} from '@rematter/frontend-lib-api-client/grpc/inventory/v1/inventory_tag_pb';
import { BookingStatus } from '@rematter/frontend-lib-api-client/grpc/trade/booking_pb';
import {
  LoadStatus,
  SalesItemLineType,
} from '@rematter/frontend-lib-api-client/grpc/trade/load_pb';
import { PurchaseOrderStatus } from '@rematter/frontend-lib-api-client/grpc/trade/purchase_order_pb';
import {
  SalesOrderStatus,
  SalesOrderType,
} from '@rematter/frontend-lib-api-client/grpc/trade/salesOrder_pb';
import { AssetStatus } from '@rematter/frontend-lib-api-client/grpc/transportation/v1/asset_pb';
import { DriverStatus } from '@rematter/frontend-lib-api-client/grpc/transportation/v1/driver_pb';
import { RouteStatus } from '@rematter/frontend-lib-api-client/grpc/transportation/v1/route_pb';
import { ServiceStatus } from '@rematter/frontend-lib-api-client/grpc/transportation/v1/service_pb';
import { TrailerStatus } from '@rematter/frontend-lib-api-client/grpc/transportation/v1/trailer_pb';
import { TruckStatus } from '@rematter/frontend-lib-api-client/grpc/transportation/v1/truck_pb';
import { Unit } from '@rematter/frontend-lib-api-client/grpc/types_pb';

export const salesOrderTypeLabels: Record<SalesOrderType, string> = {
  [SalesOrderType.UNSPECIFIED]: 'Unspecified',
  [SalesOrderType.DOMESTIC]: 'Domestic',
  [SalesOrderType.INTERNATIONAL]: 'International',
};

export const salesOrderStatusLabels: Record<SalesOrderStatus, string> = {
  [SalesOrderStatus.UNSPECIFIED]: 'Unspecified',
  [SalesOrderStatus.DRAFT]: 'Draft',
  [SalesOrderStatus.VOIDED]: 'Voided',
  [SalesOrderStatus.OPEN]: 'Open',
  [SalesOrderStatus.FINISHED]: 'Finished',
};

export const bookingStatusLabels: Record<BookingStatus, string> = {
  [BookingStatus.UNSPECIFIED]: 'Unspecified',
  [BookingStatus.VOIDED]: 'Voided',
  [BookingStatus.DRAFT]: 'Draft',
  [BookingStatus.OPEN]: 'Open',
  [BookingStatus.SHIPPED]: 'Shipped',
  [BookingStatus.COMPLETED]: 'Completed',
};

export const loadStatusLabels: Record<LoadStatus, string> = {
  [LoadStatus.UNSPECIFIED]: 'Unspecified',
  [LoadStatus.PENDING]: 'Pending',
  [LoadStatus.RECONCILED]: 'Reconciled',
  [LoadStatus.SHIPPED]: 'Shipped',
  [LoadStatus.RECEIVED]: 'Received',
  [LoadStatus.UNASSIGNED]: 'Unassigned',
  [LoadStatus.VOIDED]: 'Voided',
};

export const salesItemLineTypeLabels: Record<SalesItemLineType, string> = {
  [SalesItemLineType.UNSPECIFIED]: 'Unspecified',
  [SalesItemLineType.RAW]: 'Raw',
  [SalesItemLineType.TAGGED]: 'Tagged',
};

export const purchaseOrderStatusLabels: Record<PurchaseOrderStatus, string> = {
  [PurchaseOrderStatus.UNSPECIFIED]: 'Unspecified',
  [PurchaseOrderStatus.DRAFT]: 'Draft',
  [PurchaseOrderStatus.VOIDED]: 'Voided',
  [PurchaseOrderStatus.OPEN]: 'Open',
  [PurchaseOrderStatus.CLOSED]: 'Closed',
};

export const unitLabels: Record<Unit, string> = {
  [Unit.UNSPECIFIED]: 'Unspecified',
  [Unit.POUNDS]: 'Pounds',
  [Unit.NET_TONS]: 'Net Tons',
  [Unit.GROSS_TONS]: 'Gross Tons',
  [Unit.KILOGRAMS]: 'Kilograms',
  [Unit.METRIC_TONS]: 'Metric Tons',
  [Unit.SHORT_TONS]: 'Short Tons',
  [Unit.EACH]: 'Each',
  [Unit.SHORT_HUNDREDWEIGHT]: 'Short Hundredweight',
  [Unit.LONG_HUNDREDWEIGHT]: 'Long Hundredweight',
  [Unit.YARD]: 'Yard',
  [Unit.METER]: 'Meter',
};

export const billStatusLabels: Record<BillStatus, string> = {
  [BillStatus.UNSPECIFIED]: 'Unspecified',
  [BillStatus.PENDING]: 'Pending',
  [BillStatus.PAID]: 'Paid',
  [BillStatus.VOIDED]: 'Voided',
};

export const billTypeLabels: Record<BillType, string> = {
  [BillType.UNSPECIFIED]: 'Unspecified',
  [BillType.INBOUND]: 'Inbound',
  [BillType.OUTBOUND]: 'Outbound',
  [BillType.FREIGHT]: 'Freight',
  [BillType.ACCRUAL]: 'Accrual',
};

export const invoiceStatusLabels: Record<InvoiceStatus, string> = {
  [InvoiceStatus.UNSPECIFIED]: 'Unspecified',
  [InvoiceStatus.PROFORMA]: 'Proforma',
  [InvoiceStatus.PENDING]: 'Pending',
  [InvoiceStatus.PAID]: 'Paid',
  [InvoiceStatus.VOIDED]: 'Voided',
};

export const invoiceTypeLabels: Record<InvoiceType, string> = {
  [InvoiceType.UNSPECIFIED]: 'Unspecified',
  [InvoiceType.INBOUND]: 'Inbound',
  [InvoiceType.OUTBOUND]: 'Outbound',
  [InvoiceType.FREIGHT]: 'Freight',
  [InvoiceType.ACCRUAL]: 'Accrual',
};

export const paymentTermLabels: Record<PaymentTermCode, string> = {
  [PaymentTermCode.UNSPECIFIED]: 'Unspecified',
  [PaymentTermCode.CAD]: 'Cash Against Documents',
  [PaymentTermCode.CIA]: 'Cash In Advance',
  [PaymentTermCode.CND]: 'Cash Next Delivery',
  [PaymentTermCode.COD]: 'Cash On Delivery',
  [PaymentTermCode.EOM]: 'End of Month',
  [PaymentTermCode.LOC]: 'Letter of Credit',
  [PaymentTermCode.NET5]: 'Net 5',
  [PaymentTermCode.NET10]: 'Net 10',
  [PaymentTermCode.NET15]: 'Net 15',
  [PaymentTermCode.NET20]: 'Net 20',
  [PaymentTermCode.NET30]: 'Net 30',
  [PaymentTermCode.NET45]: 'Net 45',
  [PaymentTermCode.NET60]: 'Net 60',
  [PaymentTermCode.NET90]: 'Net 90',
  [PaymentTermCode.PIA]: 'Payment In Advance',
  [PaymentTermCode.DUE_UPON_RECEIPT]: 'Due Upon Receipt',
};

export const paymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.UNSPECIFIED]: 'Unspecified',
  [PaymentMethod.CASH]: 'Cash',
  [PaymentMethod.CHECK]: 'Check',
  [PaymentMethod.ACH]: 'ACH',
  [PaymentMethod.ATM]: 'ATM',
  [PaymentMethod.WIRE]: 'Wire',
  [PaymentMethod.WRITE_OFF]: 'Write Off',
};

export const accountTypeLabels: Record<AccountType, string> = {
  [AccountType.UNSPECIFIED]: 'Unspecified',
  [AccountType.COMMERCIAL]: 'Commercial',
  [AccountType.INDIVIDUAL]: 'Individual',
};

export const filePermissionActionLabels: Record<FilePermissionAction, string> =
  {
    [FilePermissionAction.UNSPECIFIED]: 'Unspecified',
    [FilePermissionAction.GRANT]: 'Grant',
    [FilePermissionAction.REVOKE]: 'Revoke',
  };

export const inventoryTagStatusLabels: Record<InventoryTagStatus, string> = {
  [InventoryTagStatus.UNSPECIFIED]: 'Unspecified',
  [InventoryTagStatus.ACTIVE]: 'Active',
  [InventoryTagStatus.REVISED]: 'Revised',
  [InventoryTagStatus.CANCELLED]: 'Cancelled',
  [InventoryTagStatus.CONFIRMED]: 'Confirmed',
  [InventoryTagStatus.DRAFT]: 'Draft',
  [InventoryTagStatus.IN_INVENTORY]: 'In Inventory',
  [InventoryTagStatus.IN_TRANSFER]: 'In Transfer',
  [InventoryTagStatus.LOADED]: 'Loaded',
  [InventoryTagStatus.PENDING_PURCHASE]: 'Pending Purchase',
  [InventoryTagStatus.RESERVED]: 'Reserved',
  [InventoryTagStatus.SHIPPED]: 'Shipped',
  [InventoryTagStatus.SOLD]: 'Sold',
  [InventoryTagStatus.TRANSFERRED]: 'Transferred',
  [InventoryTagStatus.VOIDED]: 'Voided',
};

export const tagTypeLabels: Record<TagType, string> = {
  [TagType.UNSPECIFIED]: 'Unspecified',
  [TagType.WORK_IN_PROGRESS]: 'Work in Progress',
  [TagType.FINISHED_GOOD]: 'Finished Good',
};

export const quantityCaptureMethodLabels: Record<
  QuantityCaptureMethod,
  string
> = {
  [QuantityCaptureMethod.UNSPECIFIED]: 'Unspecified',
  [QuantityCaptureMethod.AUTOMATIC]: 'Automatic',
  [QuantityCaptureMethod.MANUAL]: 'Manual',
};

export const assetStatusLabels: Record<AssetStatus, string> = {
  [AssetStatus.UNSPECIFIED]: 'Unspecified',
  [AssetStatus.INVENTORY]: 'Inventory',
  [AssetStatus.DEPLOYED]: 'Deployed',
  [AssetStatus.IN_TRANSIT]: 'In Transit',
  [AssetStatus.OUT_OF_ORDER]: 'Out of Order',
};

export const driverStatusLabels: Record<DriverStatus, string> = {
  [DriverStatus.UNSPECIFIED]: 'Unspecified',
  [DriverStatus.AVAILABLE]: 'Available',
  [DriverStatus.ON_DUTY]: 'On Duty',
  [DriverStatus.OFF_DUTY]: 'Off Duty',
  [DriverStatus.ON_BREAK]: 'On Break',
  [DriverStatus.INACTIVE]: 'Inactive',
};

export const truckStatusLabels: Record<TruckStatus, string> = {
  [TruckStatus.UNSPECIFIED]: 'Unspecified',
  [TruckStatus.AVAILABLE]: 'Available',
  [TruckStatus.IN_USE]: 'In Use',
  [TruckStatus.INACTIVE]: 'Inactive',
  [TruckStatus.OUT_OF_SERVICE]: 'Out of Service',
};

export const trailerStatusLabels: Record<TrailerStatus, string> = {
  [TrailerStatus.UNSPECIFIED]: 'Unspecified',
  [TrailerStatus.AVAILABLE]: 'Available',
  [TrailerStatus.IN_USE]: 'In Use',
  [TrailerStatus.INACTIVE]: 'Inactive',
  [TrailerStatus.OUT_OF_SERVICE]: 'Out of Service',
};

export const routeStatusLabels: Record<RouteStatus, string> = {
  [RouteStatus.UNSPECIFIED]: 'Unspecified',
  [RouteStatus.PLANNED]: 'Planned',
  [RouteStatus.ACTIVE]: 'Active',
  [RouteStatus.COMPLETED]: 'Completed',
  [RouteStatus.FAILED]: 'Failed',
  [RouteStatus.VOIDED]: 'Voided',
  [RouteStatus.PAUSED]: 'Paused',
  [RouteStatus.IN_PROGRESS]: 'In Progress',
};

export const serviceStatusLabels: Record<ServiceStatus, string> = {
  [ServiceStatus.ACTIVE]: 'Active',
  [ServiceStatus.COMPLETED]: 'Completed',
  [ServiceStatus.DRAFT]: 'Draft',
  [ServiceStatus.FAILED]: 'Failed',
  [ServiceStatus.IN_PROGRESS]: 'In Progress',
  [ServiceStatus.PAUSED]: 'Paused',
  [ServiceStatus.PLANNED]: 'Planned',
  [ServiceStatus.UNSPECIFIED]: 'Unspecified',
  [ServiceStatus.VOIDED]: 'Voided',
};

export const chatStatusLabels: Record<ChatStatus, string> = {
  [ChatStatus.UNSPECIFIED]: 'Unspecified',
  [ChatStatus.ACTIVE]: 'Active',
  [ChatStatus.ARCHIVED]: 'Archived',
};

export const chatMessageRoleLabels: Record<ChatMessageRole, string> = {
  [ChatMessageRole.UNSPECIFIED]: 'Unspecified',
  [ChatMessageRole.USER]: 'User',
  [ChatMessageRole.ASSISTANT]: 'Assistant',
  [ChatMessageRole.SYSTEM]: 'System',
};
